/* prettier-ignore */

import { Component, computed, inject, input, InputSignal, signal, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NewCustomRouteService } from '../../new-custom-route/new-custom-route.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SharedModule } from 'src/app/shared/shared.module';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SendUpdateRouteService } from '../send-update-route.service';
import { ModalController } from '@ionic/angular';
import { NavigationService } from 'src/app/services/navigation.service';
import { catchError, of } from 'rxjs';
import { ToolsService } from '../../../services/tools.service';
import { AuthService } from '../../../services/auth.service';
import { DebugService } from 'src/app/services/debug.service';
import { LegalTextsComponent } from 'src/app/shared/legal-texts/legal-texts.component';
import { SurveyComponent } from '../../new-survey/new-survey.component';
import Swal from 'sweetalert2';

@Component({
    selector: 'send-route',
    imports: [
        CommonModule,
        TranslateModule,
        SharedModule,
        ReactiveFormsModule,
        SharedModule
    ],
    standalone: true,
    templateUrl: './send-route.component.html',
    styleUrls: ['./send-route.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class SendRouteComponent {
    private debugService = inject(DebugService);
    
    public isInfoRoute: InputSignal<boolean> = input<boolean>(false);
    public isSaveRoute: InputSignal<any> = input<any>(false);

    private routeService = inject(NewCustomRouteService);
    private navigationService = inject(NavigationService)
    private sendUpdateService = inject(SendUpdateRouteService);
    private formBuilder = inject(FormBuilder);
    private modalController = inject(ModalController); 
    public translate = inject(TranslateService);
    public authService = inject(AuthService)
    public tools = inject(ToolsService);
    public route = computed(() => this.routeService.route());
    public registerAfter = computed(() => this.sendUpdateService.registerAfterQuestion());
    public isInfoRoutePage = computed(() => this.tools.isInfoRoutePage());
    public sendingMail = signal(false);
    public sendForm: any;
    private routeName: any = {};

    ngOnInit(){
        this.initForm();
    }

    initForm(){
        if (this.isSaveRoute()){
            this.sendForm = new FormGroup({
                routeName: new FormControl('', [Validators.required, Validators.minLength(3)]),
                additionalInfo: new FormControl(''),
                pdfs: this.formBuilder.array([])
            });
            return;
        }
        this.sendForm = new FormGroup({
            name: new FormControl('', [Validators.required, Validators.minLength(3)]),
            email: new FormControl('', [Validators.required, Validators.email]),
            additionalInfo: new FormControl(''),
            acceptConditions: new FormControl(false, [Validators.requiredTrue]),
            pdfs: this.formBuilder.array([])
        });
    }

    sendRoute() {
        const errors = [];
        if (!this.sendForm.valid) {
            for (const controlKey in this.sendForm.controls) {
                const control = this.sendForm.get(controlKey);
                if (control.errors) {
                    for (const error in control.errors) {
                        if (error == 'required' && controlKey == 'routeName') 
                            errors.push(this.translate.instant('El campo de la ruta es obligatorio'))

                        if (error == 'required' && controlKey == 'name') 
                            errors.push(this.translate.instant('El campo Nombre es obligatorio'))

                        if (error == 'minlength' && controlKey == 'name') 
                            errors.push(this.translate.instant('El campo Nombre debe de tener al menos 3 caracteres'))

                        if (error == 'required' && controlKey == 'email') 
                            errors.push(this.translate.instant('El campo Email es obligatorio'))

                        if (error == 'email' && controlKey == 'email') 
                            errors.push(this.translate.instant('El Email no es correcto'))

                        if (error == 'required' && controlKey == 'acceptConditions') 
                            errors.push(this.translate.instant('Debes aceptar los términos y condiciones'));
                    }
                }
            }
            const formatErrors = errors.map(error => `<p style='margin: 0; color: red;'>${error}</p>`)
            const errorSwalString = formatErrors.join('');
            Swal.fire('Oops...', errorSwalString, 'error');
            return;
        }

        if(this.sendUpdateService?.isRegisterAfterError() && !this.isInfoRoutePage()){
            this.sendUpdateService?.showRegisterAfterError?.update(prev => true);
            Swal.fire("Oops...", this.translate.instant('Hay preguntas sin responder'), "warning");
            return;
        }

        this.sendUpdateService?.showRegisterAfterError?.update(prev => false);
        this.sendingMail.set(true)

        const isEnteredName = Object.values(this.routeName)?.some(text => text && text !== '');
        const formValue = {
            name: this.sendForm.get('name')?.value,
            email: this.sendForm.get('email')?.value,
            additionalInfo: this.sendForm.get('additionalInfo')?.value,
            routeName: isEnteredName ? this.routeName : null,
        }        

        this.sendUpdateService.saveRoute(formValue)
        .pipe(
            catchError(() => {
                this.sendingMail.set(false);
                this.showError();
                return of();
            })
        )
        .subscribe(response => {
            this.debugService.debug(response, '[SendRouteComponent] Submit form API Response');
            this.sendingMail.set(false)
            const routeId = response.data?.id;
            const routeText = this.translate.instant(`Su ruta ha sido ${this.isSaveRoute() ? 'guardada' : 'enviada'}`);
            const pdfSend = this.translate.instant(`Su formulario ha sido enviada`);
            if (response.status == 'OK'){
                Swal.fire({
                    title: '',
                    text: this.isInfoRoute() ? pdfSend : routeText,
                    icon: 'success',
                    showCancelButton: false,
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: 'OK',
                    timer: 2500,
                    timerProgressBar: true,
                }).then((result) => {
                    this.checkIfSurvey(routeId)
                })
                return;
            }
            this.showError();
        });
    }

    showError(){
        if (this.isSaveRoute()){
            Swal.fire({ icon: 'error', title: '', text: this.translate.instant(`Ha ocurrido un error al guardar la ruta`) })
            return;
        }
        Swal.fire({ icon: 'error', title: '', text: this.translate.instant(`Ha ocurrido un error al enviar la ruta`) })
    }

    focusin(evt) {
        evt.stopPropagation();
    }

    goBack() {
        if(this.isInfoRoutePage()) {
            window.location.reload();
            return;
        }
        this.navigationService.goBack();
        
    }

    async checkIfSurvey(id) {
        if (!id || !this.sendUpdateService.survey() || this.isSaveRoute() || this.isInfoRoutePage()){
            this.goBack();
            this.sendUpdateService.userData.update(prev => ({...prev, id: id}))
            return;
        }

        const surveyModal = await this.modalController.create({
            component: SurveyComponent,
            cssClass: 'surveyModal',
            componentProps: { registerID: id, surveyModal: this.sendUpdateService.survey(), isModal: true }
        });

        await surveyModal.present();
        await surveyModal.onDidDismiss();
        this.goBack();
        this.sendUpdateService.userData.update(prev => ({...prev, id: id}))
    }

    async openPrivacyModal(){
        const privacyModal = await this.modalController.create({
            component: LegalTextsComponent,
            cssClass: 'pdfModal',
            componentProps: { type: 'privacy_policy'}
        });
        await privacyModal.present();
        await privacyModal.onDidDismiss();
    }

    insertText(value, lang){
        this.routeName[lang] = value;
        this.sendUpdateService.item.update(prev => ({...prev, name: this.routeName}))
    }

}